import React, { useState, useEffect, useRef } from 'react';
import Message from './components/Message';
import WhatsAppButton from './components/WhatsAppButton';
import Dashboard from './components/Dashboard';
import { getLocalResponse } from './qna';

const CHAT_STORAGE_KEY = 'vertex_ai_chat_history';
const PROFILE_STORAGE_KEY = 'vertex_profile';
const STATS_STORAGE_KEY = 'vertex_offline_stats';

function App() {
  // Load messages from localStorage on component mount
  const [messages, setMessages] = useState(() => {
    const savedMessages = localStorage.getItem(CHAT_STORAGE_KEY);
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        return parsed.length > 0 ? parsed : getInitialMessage();
      } catch (error) {
        console.error('Error parsing saved messages:', error);
        return getInitialMessage();
      }
    }
    return getInitialMessage();
  });
  
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [activeView, setActiveView] = useState('chat'); // 'chat' | 'dashboard'
  const messagesEndRef = useRef(null);

  // Helper function to get initial welcome message
  function getInitialMessage() {
    return [
      { 
        sender: 'bot', 
        text: "Hello! Welcome to Vertex AI Tec customer support. I'm here to help you with our services and support. How can I assist you today?", 
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
      }
    ];
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getProfile = () => {
    try {
      return JSON.parse(localStorage.getItem(PROFILE_STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  };

  const bumpOfflineStats = (intent, kind) => {
    try {
      const current = JSON.parse(localStorage.getItem(STATS_STORAGE_KEY) || '{}');
      const next = {
        totalUserMessages: current.totalUserMessages || 0,
        totalBotMessages: current.totalBotMessages || 0,
        intents: current.intents || {},
        lastActiveAt: new Date().toISOString(),
      };
      if (kind === 'user') next.totalUserMessages += 1;
      if (kind === 'bot') next.totalBotMessages += 1;
      if (intent) next.intents[intent] = (next.intents[intent] || 0) + 1;
      localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim()) return;

    const messageText = inputMessage.trim();
    const userMessage = {
      sender: 'user',
      text: messageText,
      time: getCurrentTime()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    bumpOfflineStats(null, 'user');

    try {
      // Completely offline: no fetch, no backend calls.
      const profile = getProfile();
      const offline = getLocalResponse(messageText, { profile });

      // Small delay so the typing indicator is visible.
      await new Promise((r) => setTimeout(r, 450));

      setMessages(prev => [
        ...prev,
        { sender: 'bot', text: offline.text, time: getCurrentTime() }
      ]);
      bumpOfflineStats(offline.intent, 'bot');
    } catch (error) {
      console.error('Offline reply error:', error);
      setMessages(prev => [
        ...prev,
        {
          sender: 'bot',
          text: "Sorry—I'm in offline mode and couldn't generate a reply. Please try again.",
          time: getCurrentTime()
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const clearChat = () => {
    const initialMessage = getInitialMessage();
    setMessages(initialMessage);
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(initialMessage));
  };

  // Removed form handling functions - no longer needed

  return (
    <div className="min-h-[100dvh] bg-dark-bg flex items-stretch sm:items-center justify-center p-0 sm:p-4 pb-24 sm:pb-4">
      <div className="w-full sm:max-w-2xl h-[100dvh] sm:h-[600px] bg-gray-800 rounded-none sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="bg-header-bg px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
              <span className="text-white text-lg">🤖</span>
            </div>
            <div className="flex-1">
              <h2 className="text-white font-semibold text-base sm:text-lg">Vertex AI Tec Support</h2>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 text-sm">Online</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveView(activeView === 'chat' ? 'dashboard' : 'chat')}
              className="text-gray-300 hover:text-white transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-gray-700 border border-gray-700"
              title={activeView === 'chat' ? 'Open dashboard' : 'Back to chat'}
            >
              {activeView === 'chat' ? 'Dashboard' : 'Chat'}
            </button>
            {activeView === 'chat' && (
              <button
                onClick={clearChat}
                className="text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-gray-700"
                title="Clear chat history"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {activeView === 'dashboard' ? (
          <Dashboard messages={messages} onClose={() => setActiveView('chat')} />
        ) : (
          <>
            {/* Chat Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {messages.map((message, index) => (
                <Message 
                  key={index} 
                  message={message} 
                />
              ))}
              
              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex justify-start mb-4 animate-fade-in">
                  <div className="bg-bot-bubble text-white px-4 py-2 rounded-2xl rounded-bl-md">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="bg-header-bg px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-700">
              <form onSubmit={handleSendMessage} className="flex gap-2 sm:gap-3">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask anything (offline fallback is enabled)..."
                  className="flex-1 bg-gray-700 text-white placeholder-gray-400 px-4 py-3 rounded-xl border border-gray-600 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent focus:ring-opacity-20 transition-all duration-200"
                />
                <button
                  type="submit"
                  disabled={!inputMessage.trim()}
                  className="shrink-0 bg-accent hover:bg-accent/80 disabled:bg-gray-600 disabled:cursor-not-allowed text-white p-3 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                  </svg>
                </button>
              </form>
            </div>
          </>
        )}
      </div>
      
      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
}

export default App;
